import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Container } from '@/components/layout/Container'
import { Button } from '@/components/ui/Button'
import { useAuth } from '@/hooks/useAuth'
import { useUIStore } from '@/stores/uiStore'
import { useSettings } from '@/hooks/useSettings'
import { fetchStreak, recordStreakVisit, type CustomerStreak } from '@/lib/api'
import { cn } from '@/lib/utils'

const MILESTONE = 3

/**
 * Daily check-in for signed-in members. The visit is only ever posted when the
 * member presses the button — the API deliberately does not track a silent
 * visitor — so this reads as a ritual rather than surveillance.
 */
export function StreakStrip() {
  const { isSignedIn, loading } = useAuth()
  const addToast = useUIStore((s) => s.addToast)
  const openAuthModal = useUIStore((s) => s.openAuthModal)
  const { promo_modules } = useSettings()

  const promo = promo_modules?.streak
  const promoEnabled = promo?.enabled === true

  const [streak, setStreak] = useState<CustomerStreak | null | undefined>(undefined)
  const [checking, setChecking] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [justUnlocked, setJustUnlocked] = useState(false)

  useEffect(() => {
    if (!isSignedIn) {
      setStreak(null)
      return
    }
    let cancelled = false
    fetchStreak().then((s) => {
      if (!cancelled) setStreak(s)
    })
    return () => {
      cancelled = true
    }
  }, [isSignedIn])

  const checkIn = useCallback(async () => {
    setChecking(true)
    setError(null)
    try {
      const next = await recordStreakVisit()
      if (!next) {
        setError('We could not check you in. Please try again.')
        return
      }
      const unlocked = next.mystery && streak?.mystery?.code !== next.mystery.code
      setStreak(next)
      if (unlocked && next.mystery) {
        setJustUnlocked(true)
        addToast(`Day ${next.current}: ${next.mystery.label}`, 'success')
      } else {
        addToast(`Day ${next.current} of your streak`, 'success')
      }
    } finally {
      setChecking(false)
    }
  }, [addToast, streak])

  // Admin-controlled streak promo takes precedence when enabled.
  if (promoEnabled) {
    return (
      <section className="border-y border-ink/10 bg-paper">
        <Container className="flex flex-col items-start gap-4 py-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-wd-orange">{promo?.headline || 'Sky streak'}</p>
            <p className="mt-1 text-sm text-carbon/70">
              {promo?.body || 'Check in on days you visit. Three in a row unlocks a mystery reward.'}
            </p>
          </div>
          {!isSignedIn && (
            promo?.ctaLabel ? (
              <Link to="/account/dashboard">
                <Button variant="secondary">{promo.ctaLabel}</Button>
              </Link>
            ) : (
              <Button variant="secondary" onClick={() => openAuthModal()}>
                Sign in to start
              </Button>
            )
          )}
        </Container>
      </section>
    )
  }

  if (loading) return null
  if (!isSignedIn) {
    return (
      <section className="border-y border-ink/10 bg-paper">
        <Container className="flex flex-col items-start gap-4 py-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-wd-orange">Sky streak</p>
            <p className="mt-1 text-sm text-carbon/70">
              Check in on days you visit. Three in a row unlocks a mystery reward.
            </p>
          </div>
          <Button variant="secondary" onClick={() => openAuthModal()}>
            Sign in to start
          </Button>
        </Container>
      </section>
    )
  }

  if (streak === undefined) return null

  const current = streak?.current ?? 0
  const mystery = streak?.mystery
  const remaining = streak?.nextRewardAt ? Math.max(0, streak.nextRewardAt - current) : 0

  return (
    <section className="border-y border-ink/10 bg-paper">
      <Container className="flex flex-col gap-5 py-5 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-5">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-wd-orange">Sky streak</p>
            <p className="mt-1 font-display text-2xl normal-case text-carbon">
              {current === 0 ? 'Start your streak' : `Day ${current}`}
              {streak && streak.longest > current && (
                <span className="ml-2 align-middle text-xs font-normal tracking-wide text-carbon/40">
                  best {streak.longest}
                </span>
              )}
            </p>
          </div>

          {!mystery && (
            <div className="flex items-center gap-2" aria-label={`${current} of ${MILESTONE} days`}>
              {Array.from({ length: MILESTONE }).map((_, i) => (
                <span
                  key={i}
                  className={cn(
                    'h-2.5 w-2.5 rounded-full transition-colors',
                    i < Math.min(current, MILESTONE) ? 'bg-wd-orange' : 'bg-ink/15'
                  )}
                />
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-4">
          {mystery ? (
            <>
              <p className="text-sm text-carbon/70">
                {justUnlocked ? 'Unlocked: ' : 'Your reward: '}
                <span className="text-carbon">{mystery.label}</span>
              </p>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard?.writeText(mystery.code)
                  addToast(`${mystery.code} copied`, 'success')
                }}
                className="border border-ink/20 px-3 py-2 font-mono text-xs tracking-widest text-carbon transition-colors hover:border-wd-orange hover:text-wd-orange"
              >
                {mystery.code}
              </button>
            </>
          ) : (
            <>
              <p className="text-sm text-carbon/60">
                {remaining > 0
                  ? `${remaining} more ${remaining === 1 ? 'day' : 'days'} to a mystery reward`
                  : 'A mystery reward is waiting'}
              </p>
              <Button
                variant={streak?.activeToday ? 'secondary' : 'primary'}
                isLoading={checking}
                disabled={streak?.activeToday}
                onClick={checkIn}
              >
                {streak?.activeToday ? 'Checked in today' : 'Check in today'}
              </Button>
            </>
          )}
        </div>

        {error && <p className="w-full text-xs text-carbon">{error}</p>}
      </Container>
    </section>
  )
}
